import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ChevronDown } from "lucide-react";

const MaskedLine = ({ children, delay, opened, className = "" }) => (
    <span className={`block overflow-hidden ${className}`}>
        <motion.span
            className="block"
            initial={{ y: "115%" }}
            animate={opened ? { y: 0 } : {}}
            transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

export default function Hero({ opened }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0.15]);

    return (
        <header ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 pb-16 pt-20 text-center">
            <motion.div className="absolute inset-0" style={{ y: imgY }}>
                <img
                    src="/photos/couple-hero.webp"
                    alt="Vijay Laxmi and Dileep together"
                    data-testid="hero-couple-photo"
                    className="h-full w-full scale-[1.12] object-cover lg:scale-100 lg:object-contain lg:object-right"
                />
                <div
                    className="absolute inset-0 lg:hidden"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(250,246,240,0.84) 0%, rgba(250,246,240,0.9) 50%, rgba(250,246,240,0.97) 100%)",
                    }}
                />
                <div
                    className="absolute inset-0 hidden lg:block"
                    style={{
                        background:
                            "linear-gradient(90deg, rgba(250,246,240,0.97) 0%, rgba(250,246,240,0.88) 34%, rgba(250,246,240,0.45) 62%, rgba(250,246,240,0.15) 100%)",
                    }}
                />
            </motion.div>

            <motion.div style={{ opacity: fade }} className="relative z-10 flex flex-col items-center">
                <MaskedLine opened={opened} delay={0.15}>
                    <span className="font-hindi text-base sm:text-lg" style={{ color: "var(--wine)" }} data-testid="hero-blessing">
                        ॥ श्री गणेशाय नमः ॥
                    </span>
                </MaskedLine>

                <MaskedLine opened={opened} delay={0.3} className="mt-6">
                    <span className="eyebrow">With the blessings of our families</span>
                </MaskedLine>

                <h1 className="font-display mt-4 leading-[0.95]" data-testid="hero-couple-names">
                    <MaskedLine opened={opened} delay={0.45}>
                        <span className="text-5xl tracking-tight sm:text-7xl lg:text-8xl" style={{ color: "var(--wine)" }}>
                            Vijay Laxmi
                        </span>
                    </MaskedLine>
                    <MaskedLine opened={opened} delay={0.58}>
                        <span className="my-1 block text-3xl italic sm:text-4xl" style={{ color: "var(--gold)" }}>
                            weds
                        </span>
                    </MaskedLine>
                    <MaskedLine opened={opened} delay={0.7}>
                        <span className="text-5xl tracking-tight sm:text-7xl lg:text-8xl" style={{ color: "var(--wine)" }}>
                            Dileep
                        </span>
                    </MaskedLine>
                </h1>

                <MaskedLine opened={opened} delay={1}>
                    <span
                        data-testid="hero-date"
                        className="mt-8 inline-block rounded-full px-6 py-2 text-xs font-semibold uppercase tracking-[0.25em]"
                        style={{ border: "1px solid rgba(212,175,55,0.5)", color: "var(--wine)", background: "rgba(255,253,250,0.7)" }}
                    >
                        24 · 11 · 2026 · Bera Hambro Ki Bawri, Rajasthan
                    </span>
                </MaskedLine>
            </motion.div>

            <motion.a
                href="#story"
                data-testid="scroll-explore-link"
                className="relative z-10 mt-16 flex flex-col items-center gap-2 text-xs uppercase tracking-[0.3em]"
                style={{ color: "var(--muted)" }}
                initial={{ opacity: 0 }}
                animate={opened ? { opacity: 1 } : {}}
                transition={{ delay: 1.6, duration: 0.8 }}
            >
                Scroll to explore
                <ChevronDown size={16} className="float-slow" style={{ color: "var(--gold)" }} />
            </motion.a>
        </header>
    );
}
